import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingTable } from "@/components/ui/system-state";
import { EmptyState } from "@/components/ui/feedback";
import { NotAvailableState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { HotWalletTabs } from "@/features/hot-wallets/hot-wallet-tabs";
import { useHotWallets } from "@/hooks/use-hot-wallets";
import { useHotWalletDetail } from "@/hooks/use-hot-wallet-detail";
import { useHotWalletsLocale } from "@/i18n/use-hot-wallets-locale";

/**
 * HW-S1: hot wallets overview (SPEC-HW-8, WORKFLOW.md §3).
 *
 * `/hot-wallets` renders the list; `/hot-wallets/:walletId` renders one
 * wallet's overview with the shared tab bar. Both are wired to the real,
 * typed gateway clients in lib/api.ts (GET /v1/hot-wallets[, /{id}]) — no
 * route exists behind the gateway yet (HW-L1/L8 land it), so today's honest
 * outcome is the unavailable state built from the real failed response,
 * never a fabricated balance, velocity bar, or gas-tank reading.
 */
export default function HotWalletsDashboardPage() {
  const { walletId } = useParams<{ walletId?: string }>();
  const { ui, dir } = useHotWalletsLocale();

  return walletId ? (
    <HotWalletDetailView walletId={walletId} ui={ui} dir={dir} />
  ) : (
    <HotWalletsListView ui={ui} dir={dir} />
  );
}

function HotWalletsListView({ ui, dir }: { ui: Record<string, string>; dir: "ltr" | "rtl" }) {
  const { wallets, loading, error, refresh } = useHotWallets();

  return (
    <div dir={dir}>
      <PageHeader title={ui["dashboard.title"]} subtitle={ui["dashboard.subtitle"]} />

      {loading && <LoadingTable rows={4} cols={5} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2">
          <NotAvailableState
            title={ui["dashboard.unavailableTitle"]}
            description={ui["dashboard.unavailableDescription"]}
            reason={error.reasonCode}
          />
          <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          {/* A bare text button is ~20px tall on a phone; the lane's
              tap-target floor is 36px, so the hit area is padded without
              changing the label's visual weight. */}
          <button
            type="button"
            onClick={refresh}
            className="inline-flex min-h-[36px] items-center text-sm font-medium text-primary underline"
          >
            {ui["common.retry"]}
          </button>
        </div>
      )}

      {!loading && !error && wallets.length === 0 && (
        <EmptyState card title={ui["dashboard.emptyTitle"]} description={ui["dashboard.emptyDescription"]} />
      )}

      {!loading && !error && wallets.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {wallets.map((w) => (
            <li key={w.walletId}>
              <a
                href={`/hot-wallets/${encodeURIComponent(w.walletId)}`}
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-bg"
              >
                {/* A wallet id can be a long opaque string; `min-w-0` lets the
                    label truncate inside the row instead of widening it, and
                    `shrink-0` keeps the network visible at 375px. */}
                <span className="min-w-0 flex-1 truncate font-semibold text-ink">{w.label ?? w.walletId}</span>
                <span className="shrink-0 text-muted">{w.chain}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HotWalletDetailView({
  walletId,
  ui,
  dir,
}: {
  walletId: string;
  ui: Record<string, string>;
  dir: "ltr" | "rtl";
}) {
  const { wallet, loading, error, refresh } = useHotWalletDetail(walletId);

  return (
    <div dir={dir}>
      <PageHeader title={ui["dashboard.title"]} subtitle={ui["dashboard.subtitle"]} />
      <HotWalletTabs walletId={walletId} active="overview" labels={ui} />

      {loading && <LoadingTable rows={2} cols={4} />}

      {!loading && (error || !wallet) && (
        <div className="flex flex-col items-center gap-2">
          <NotAvailableState
            title={ui["dashboard.unavailableTitle"]}
            description={ui["dashboard.unavailableDescription"]}
            reason={error?.reasonCode}
          />
          {error && (
            <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          )}
          <button
            type="button"
            onClick={refresh}
            className="inline-flex min-h-[36px] items-center text-sm font-medium text-primary underline"
          >
            {ui["common.retry"]}
          </button>
        </div>
      )}

      {!loading && !error && wallet && (
        // One column on a phone, two from sm, four from lg: a half-width tile
        // at 375px leaves ~130px for the value and its label, which the
        // localized labels ("Hot share of cap") overflow.
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatPlaceholder label={ui["common.activeBadge"]} value={wallet.status ?? "—"} />
          <StatPlaceholder label="Deposit addresses" value={String(wallet.depositAddressCount ?? "—")} />
          <StatPlaceholder label="Co-signer" value={wallet.coSignerStatus ?? "—"} />
          <StatPlaceholder label="Hot share of cap" value={formatPercent(wallet.hotShareOfTotalPct)} />
        </div>
      )}
    </div>
  );
}

/**
 * A percentage the gateway did not report renders as an explicit dash. A null
 * or undefined figure is "not reported" — rendering it through a template
 * literal leaked the JS string `null%` into the stat tile, and rendering it as
 * `0%` would be worse: 0% is a real, meaningful reading (HW-S1 honest-state
 * rule, SPEC-HW-8 §3).
 */
function formatPercent(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? `${value}%` : "—";
}

function StatPlaceholder({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-[18px]">
      <div className="text-[20px] font-bold leading-[1.1] tracking-tight text-ink">{value}</div>
      <div className="mt-1 text-xs text-muted">{label}</div>
    </div>
  );
}
